import { motion } from 'framer-motion'
import { Button } from './Button'
import type { AnalysisError } from '@/services/errors'

interface ErrorMessageProps {
  error: AnalysisError
  onRetry?: () => void
  onTakeSelfie?: () => void
  onChooseFromGallery?: () => void
  isRetrying?: boolean
  retryCount?: number
  maxRetries?: number
}

// Icons for different error types
const ErrorIcon = () => (
  <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
)

const WifiOffIcon = () => (
  <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
  </svg>
)

const CameraIcon = () => (
  <svg className="w-12 h-12 text-[#46C0C6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const ServerIcon = () => (
  <svg className="w-12 h-12 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
)

function getIcon(errorType: string) {
  switch (errorType) {
    case 'offline':
    case 'network_error':
      return <WifiOffIcon />
    case 'face_not_detected':
    case 'image_quality':
      return <CameraIcon />
    case 'server_error':
    case 'analysis_timeout':
      return <ServerIcon />
    default:
      return <ErrorIcon />
  }
}

export function ErrorMessage({
  error,
  onRetry,
  onTakeSelfie,
  onChooseFromGallery,
  isRetrying = false,
  retryCount = 0,
  maxRetries = 2,
}: ErrorMessageProps) {
  const showRetryCount = retryCount > 0 && retryCount < maxRetries

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center text-center px-6 py-8"
    >
      {/* Icon */}
      <div className="mb-4">
        {getIcon(error.type)}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {error.title}
      </h3>

      {/* Message */}
      <p className="text-gray-600 mb-6 max-w-sm">
        {error.message}
      </p>

      {/* Photo tips for face/quality errors */}
      {error.suggestRetake && (
        <div className="bg-gray-50 rounded-xl p-4 mb-6 w-full max-w-sm">
          <p className="text-sm font-medium text-gray-900 mb-2">Tips for a better photo:</p>
          <ul className="text-sm text-gray-600 space-y-1.5 text-left">
            <li className="flex items-start gap-2">
              <span className="text-[#46C0C6] mt-0.5">✓</span>
              <span>Face the camera directly</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#46C0C6] mt-0.5">✓</span>
              <span>Find even, natural lighting</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#46C0C6] mt-0.5">✓</span>
              <span>Keep hair away from face</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#46C0C6] mt-0.5">✓</span>
              <span>Remove glasses if possible</span>
            </li>
          </ul>
        </div>
      )}

      {/* Retry count indicator */}
      {showRetryCount && (
        <p className="text-sm text-gray-500 mb-4">
          Retry attempt {retryCount} of {maxRetries}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        {/* Primary action: Retake if suggested, otherwise Retry */}
        {error.suggestRetake ? (
          <>
            {onTakeSelfie && (
              <Button size="lg" fullWidth onClick={onTakeSelfie}>
                Take New Selfie
              </Button>
            )}
            {onChooseFromGallery && (
              <Button variant="outline" size="md" fullWidth onClick={onChooseFromGallery}>
                Choose from Gallery
              </Button>
            )}
            {error.canRetry && onRetry && (
              <Button
                variant="secondary"
                size="md"
                fullWidth
                onClick={onRetry}
                isLoading={isRetrying}
              >
                {isRetrying ? 'Retrying...' : 'Try Again Anyway'}
              </Button>
            )}
          </>
        ) : (
          <>
            {error.canRetry && onRetry && (
              <Button
                size="lg"
                fullWidth
                onClick={onRetry}
                isLoading={isRetrying}
              >
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            )}
            {/* Secondary: New photo options for non-face errors */}
            {onTakeSelfie && (
              <Button variant="outline" size="md" fullWidth onClick={onTakeSelfie}>
                Take New Selfie
              </Button>
            )}
            {onChooseFromGallery && (
              <Button variant="secondary" size="md" fullWidth onClick={onChooseFromGallery}>
                Choose from Gallery
              </Button>
            )}
          </>
        )}
      </div>

      {/* Technical details (collapsed by default in production) */}
      {error.technicalDetails && import.meta.env.DEV && (
        <details className="mt-6 text-left w-full max-w-sm">
          <summary className="text-xs text-gray-400 cursor-pointer">
            Technical details
          </summary>
          <pre className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded overflow-auto">
            {error.technicalDetails}
          </pre>
        </details>
      )}
    </motion.div>
  )
}

/**
 * Offline Banner - shown at the top of the screen when offline
 */
export function OfflineBanner({ onDismiss }: { onDismiss?: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
        <span className="text-sm">You're offline. Some features may not work.</span>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-gray-400 hover:text-white">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </motion.div>
  )
}
